/**
 * "הגדרות קשורות" — the map of every setting in Turbo Business and which screens it shapes.
 *
 * A setting: { id, group, icon, title, description, keywords, target }
 *   target.hub    a section of the settings hub (/settings?section=<key>) — needs the "settings" menu item
 *   target.path   a screen; offered only when the user's menu has the screen (`nav` = its menu key)
 *   target.query  extra query string (e.g. tab=funds)
 *   target.focus  the title of a card inside that screen — opened and highlighted on arrival
 *   target.design the "מראה ותצוגה" block (rendered inside the panel, always available)
 *
 * A rule ties screens to settings, most relevant first, each with a short "why" for this screen.
 * First matching rule wins; `path` is the router path.
 */
export type RelatedTarget = {
  hub?: string;
  path?: string;
  nav?: string;
  query?: string;
  focus?: string;
  design?: boolean;
};

export type RelatedSetting = {
  id: string;
  group: string;
  icon: string;
  title: string;
  description: string;
  keywords?: string;
  target: RelatedTarget;
};

export type RelatedRule = {
  match: (path: string) => boolean;
  label?: string;
  items: Array<[string, string?]>;
};

const starts =
  (...prefixes: string[]) =>
  (path: string) =>
    prefixes.some((p) => path === p || path.startsWith(`${p}/`));

export const RELATED_SETTINGS: RelatedSetting[] = [
  // מראה
  { id: "design", group: "מראה ותצוגה", icon: "sun", title: "מצב תצוגה וסגנון", description: "בהיר, כהה או לפי המחשב · מודרני / קלאסי / רטרו", keywords: "כהה בהיר עיצוב צבע", target: { design: true } },
  // ארגון ומשתמשים
  { id: "users", group: "ארגון ומשתמשים", icon: "users", title: "משתמשים ותפקידים", description: "הזמנת משתמשים, תפקיד בארגון וסטטוס חברות", keywords: "משתמש הזמנה תפקיד צוות", target: { path: "/users", nav: "users" } },
  { id: "permissions", group: "ארגון ומשתמשים", icon: "key", title: "הרשאות ותבניות תפקיד", description: "מה כל תפקיד רואה ועושה — לפי מסך ופעולה", keywords: "הרשאה תפקיד גישה", target: { path: "/settings/permissions", nav: "settings" } },
  { id: "field-policies", group: "ארגון ומשתמשים", icon: "eye", title: "מדיניות שדות", description: "אילו שדות כל תפקיד רואה, עורך או לא רואה בכלל", keywords: "שדה הסתרה צפייה עריכה", target: { hub: "field-policies" } },
  { id: "recycle-bin", group: "ארגון ומשתמשים", icon: "trash", title: "סל מחזור", description: "שחזור משימות, אוטומציות וטפסים שנמחקו ב־30 הימים האחרונים", keywords: "מחיקה שחזור נמחק סל", target: { hub: "recycle-bin" } },
  { id: "guidance", group: "ארגון ומשתמשים", icon: "book", title: "מסמך הנחיות", description: "נהלי העבודה של הארגון, עם גרסאות", keywords: "נוהל הנחיה מסמך", target: { path: "/org-guidance", nav: "org_guidance" } },
  // כספים
  { id: "structure-categories", group: "כספים", icon: "folder", title: "קטגוריות", description: "קטגוריות הכנסה והוצאה, סכומים קבועים ומחזוריות", keywords: "קטגוריה מחירון סכום", target: { path: "/structure", nav: "finance_structure", query: "tab=categories" } },
  { id: "structure-funds", group: "כספים", icon: "wallet", title: "קופות פנימיות", description: "הקופות שאליהן נרשמות ההכנסות וההוצאות", keywords: "קופה פרויקט", target: { path: "/structure", nav: "finance_structure", query: "tab=funds" } },
  { id: "structure-budgets", group: "כספים", icon: "chart", title: "תקציבים", description: "תקציב לכל קטגוריה וקופה, מעקב ניצול", keywords: "תקציב ניצול", target: { path: "/structure", nav: "finance_structure", query: "tab=budgets" } },
  { id: "structure-campaigns", group: "כספים", icon: "flag", title: "מיפוי קמפיינים", description: "שיוך קמפיינים לקטגוריות ולקופות", keywords: "קמפיין מיפוי", target: { path: "/structure", nav: "finance_structure", query: "tab=campaigns" } },
  { id: "structure-nedarim", group: "כספים", icon: "swap", title: "מיפוי נדרים", description: "לאיזו קטגוריה וקופה נכנסת כל עסקת נדרים פלוס", keywords: "נדרים מיפוי עסקה", target: { path: "/structure", nav: "finance_structure", query: "tab=nedarim" } },
  // חיבורים
  { id: "nedarim", group: "חיבורים", icon: "repeat", title: "נדרים פלוס — חיבור וסנכרון", description: "מפתח API, Webhooks, כתובות IP ושליפת עסקאות", keywords: "נדרים webhook סנכרון הוק api", target: { hub: "nedarim" } },
  { id: "ezcount", group: "חיבורים", icon: "receipt", title: "EZcount — הנפקת קבלות", description: "חיבור ל-EZcount להנפקת קבלות ומסמכי הנה״ח", keywords: "קבלה חשבונית איזיקאונט", target: { hub: "ezcount" } },
  // אוטומציות וטפסים
  { id: "automations", group: "אוטומציות וטפסים", icon: "sparkle", title: "אוטומציות", description: "מיילים, משימות ודוחות שקורים לבד — לפי לוח זמנים או אירוע", keywords: "אוטומציה תזכורת מייל דוח", target: { path: "/automations", nav: "automations" } },
  { id: "forms", group: "אוטומציות וטפסים", icon: "note", title: "טפסים וקליטה", description: "טפסים ציבוריים, שיוך לשדות ומה נוצר מכל הגשה", keywords: "טופס קליטה הגשה", target: { path: "/forms", nav: "forms" } },
  { id: "mail-log", group: "אוטומציות וטפסים", icon: "inbox", title: "יומן דואר", description: "כל מייל בודד שנשלח מהמערכת והסטטוס שלו", keywords: "מייל דיוור נשלח יומן", target: { path: "/mail-log", nav: "mail_log" } },
  { id: "mailing", group: "אוטומציות וטפסים", icon: "mail", title: "דיוור", description: "דיוורים ללקוחות ולמנויים, טפסי הרשמה ורשימת חסומים", keywords: "דיוור ניוזלטר תפוצה הסרה מנויים פרסומת", target: { path: "/mailing", nav: "mailing" } },
  // אישי
  { id: "personal", group: "אישי", icon: "user", title: "אזור אישי", description: "פרטים אישיים וסיסמה", keywords: "פרופיל סיסמה", target: { path: "/personal-area", nav: "personal_area" } },
];

export const RELATED_RULES: RelatedRule[] = [
  {
    match: starts("/customers"),
    items: [
      ["field-policies", "אילו שדות של הכרטיס כל תפקיד רואה ועורך"],
      ["structure-categories", "הקטגוריות והמחירון שמהם נבנים מנויים וחובות"],
      ["nedarim", "תשלומים מנדרים פלוס מתעדכנים בכרטיס"],
      ["ezcount", "הנפקת קבלה מתוך תשלום בכרטיס"],
      ["forms", "טפסים שיוצרים או מעדכנים כרטיסים"],
      ["automations", "תזכורות ומיילים אוטומטיים ללקוחות"],
      ["mailing", "דיוור ללקוחות — עם אותם סינונים של הרשימה"],
    ],
  },
  {
    match: starts("/subscriptions"),
    items: [
      ["structure-categories", "קטגוריית המנוי, הסכום והמחזוריות"],
      ["nedarim", "הו״ק ותשלומים שנקלטים מנדרים פלוס"],
      ["structure-nedarim", "לאיזה מנוי משויכת כל עסקת נדרים"],
      ["ezcount", "קבלות על תשלומי מנויים"],
      ["automations", "תזכורת על חוב או הרשאה שעומדת לפוג"],
    ],
  },
  {
    match: starts("/income-expenses/bank"),
    items: [
      ["structure-categories", "קטגוריות לשורות שנוצרות מהבנק"],
      ["structure-funds", "לאיזו קופה נרשמת כל תנועה"],
      ["nedarim", "עסקאות נדרים מול תנועות הבנק"],
    ],
  },
  {
    match: starts("/income-expenses/receipts", "/income-expenses/accounting"),
    items: [
      ["ezcount", "החיבור שמנפיק את הקבלות והמסמכים"],
      ["structure-categories", "קטגוריות ההכנסות שעליהן מופקים מסמכים"],
    ],
  },
  {
    match: starts("/income-expenses/suppliers", "/income-expenses/payment-requests"),
    items: [
      ["structure-categories", "קטגוריות ההוצאות לספקים ולדרישות"],
      ["structure-budgets", "תקציב מול ביצוע לכל קטגוריה"],
      ["automations", "תזכורת על דרישות תשלום פתוחות"],
    ],
  },
  {
    match: starts("/income-expenses"),
    items: [
      ["structure-categories", "קטגוריות לכל הכנסה והוצאה"],
      ["structure-funds", "קופות פנימיות לרישום התנועות"],
      ["structure-budgets", "תקציבים ומעקב ניצול"],
      ["nedarim", "שליפת עסקאות מנדרים פלוס"],
      ["ezcount", "הנפקת קבלה מתוך הכנסה"],
      ["automations", "דוח הכנסות והוצאות שבועי במייל"],
    ],
  },
  {
    match: starts("/structure"),
    items: [
      ["nedarim", "החיבור שממנו מגיעות העסקאות למיפוי"],
      ["field-policies", "מי רואה ועורך את המבנה הכספי"],
      ["permissions", "הרשאות למבנה הכספי"],
    ],
  },
  {
    match: starts("/tasks"),
    items: [
      ["automations", "משימות שנוצרות לבד — לפי אירוע או לוח זמנים"],
      ["users", "למי אפשר לשייך משימות"],
      ["permissions", "מי רואה ומנהל משימות"],
      ["recycle-bin", "משימות שנמחקו — אפשר לשחזר 30 יום"],
    ],
  },
  {
    match: starts("/meetings"),
    items: [
      ["users", "משתתפים קבועים בישיבות"],
      ["automations", "תזכורת לפני ישיבה וסיכום במייל"],
      ["permissions", "מי מנהל ישיבות צוות"],
    ],
  },
  {
    match: starts("/forms"),
    items: [
      ["field-policies", "השדות שהטופס ממלא בכרטיס"],
      ["automations", "מה קורה אחרי הגשת טופס (מייל, משימה)"],
      ["mail-log", "המיילים שנשלחו להגשות"],
      ["recycle-bin", "טפסים וקליטות שנמחקו — אפשר לשחזר 30 יום"],
    ],
  },
  {
    match: starts("/mail-log"),
    items: [
      ["automations", "האוטומציות ששולחות מיילים"],
      ["forms", "מיילי אישור של טפסים"],
    ],
  },
  {
    match: starts("/automations"),
    items: [
      ["mail-log", "כל מייל שאוטומציה שלחה"],
      ["mailing", "מי שהסיר את עצמו מהדיוור לא מקבל גם מיילים של אוטומציות"],
      ["users", "הנמענים והאחראים למשימות"],
      ["recycle-bin", "אוטומציות שנמחקו — חוזרות מושהות"],
    ],
  },
  {
    match: starts("/mailing"),
    items: [
      ["automations", "מיילים אוטומטיים לפי אירוע (תודה על תשלום, ברוכים הבאים)"],
      ["mail-log", "מיילים בודדים שנשלחו מהמערכת"],
      ["forms", "טפסים שיוצרים לקוחות — שאפשר אחר כך לשלוח אליהם דיוור"],
    ],
  },
  {
    match: starts("/users", "/settings"),
    items: [
      ["permissions", "מה כל תפקיד רואה ועושה"],
      ["field-policies", "הסתרת שדות לפי תפקיד"],
      ["guidance", "נהלי העבודה שהצוות מקבל"],
    ],
  },
  {
    match: starts("/org-guidance"),
    items: [
      ["users", "מי מקבל את מסמך ההנחיות"],
      ["permissions", "מי עורך את המסמך"],
    ],
  },
  {
    match: (p) => p === "/",
    label: "תמונת מצב",
    items: [
      ["structure-categories", "הקטגוריות שמרכיבות את המדדים"],
      ["structure-budgets", "תקציב מול ביצוע"],
      ["automations", "דוח תמונת מצב שבועי במייל"],
      ["nedarim", "תשלומים שנכנסים אוטומטית"],
    ],
  },
];

export function rulesFor(path: string): RelatedRule | null {
  return RELATED_RULES.find((r) => r.match(path)) ?? null;
}
